/**
 * Phase 5D — Unified "Make a Payment" page (AUD-first, USD checkout).
 *
 * - AUD is source-of-truth. Default 1-on-1 = AUD 55. Intensive = AUD 250.
 * - Student picks display currency: AUD / USD / KES.
 * - Backend always charges in USD; FX rate from /api/public/pricing.
 * - No Paystack wording, no USD-only disclaimers.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CreditCard, ShieldCheck } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import Seo from '@/components/shared/Seo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const PRODUCT_LABELS = {
  trial_session: 'Trial Session / Booking Deposit',
  single_session: '1-on-1 Session',
  intensive_package: 'Intensive Support Package',
};

const ALLOWED_PREFILL_TYPES = new Set(['trial_session', 'single_session', 'intensive_package']);

const CURRENCIES = [
  { code: 'AUD', label: 'AUD', desc: 'Australian Dollar' },
  { code: 'USD', label: 'USD', desc: 'US Dollar' },
  { code: 'KES', label: 'KES', desc: 'Kenyan Shilling' },
];

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const prefillType = searchParams.get('type');
  const prefillEmail = searchParams.get('email') || '';
  const prefillName = searchParams.get('name') || '';

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [config, setConfig] = useState({ default_prices: {}, supported_payment_types: [] });
  const [pricing, setPricing] = useState(null); // /api/public/pricing
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [studentName, setStudentName] = useState(prefillName);
  const [email, setEmail] = useState(prefillEmail);
  const [phone, setPhone] = useState('');
  const [paymentType, setPaymentType] = useState(
    ALLOWED_PREFILL_TYPES.has(prefillType) ? prefillType : 'single_session'
  );
  const [amountAud, setAmountAud] = useState('');
  const [displayCurrency, setDisplayCurrency] = useState('AUD');
  const navigate = useNavigate();

  // Load AUD defaults + FX rates in parallel
  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/payments/config`).then((r) => r.json()).catch(() => ({})),
      fetch(`${BACKEND_URL}/api/public/pricing`).then((r) => r.json()).catch(() => null),
    ]).then(([cfg, pr]) => {
      setConfig(cfg);
      setPricing(pr);
      const seeded = cfg?.default_prices?.[paymentType];
      if (seeded != null) setAmountAud(Number(seeded).toFixed(2));
      setLoadingConfig(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fxUsd = pricing?.fx_aud_to_usd ?? 0.65;
  const fxKes = pricing?.fx_aud_to_kes ?? 86.5;

  const amountAudNum = Number(amountAud) || 0;
  const checkoutUsd = useMemo(() => Math.round(amountAudNum * fxUsd * 100) / 100, [amountAudNum, fxUsd]);
  const displayedAmount = useMemo(() => {
    if (displayCurrency === 'AUD') return amountAudNum;
    if (displayCurrency === 'USD') return checkoutUsd;
    return Math.round(amountAudNum * fxKes * 100) / 100;
  }, [displayCurrency, amountAudNum, checkoutUsd, fxKes]);

  const onTypeChange = (newType) => {
    setPaymentType(newType);
    const seeded = config.default_prices?.[newType];
    if (seeded != null) setAmountAud(Number(seeded).toFixed(2));
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!studentName.trim() || !email.trim() || amountAudNum <= 0) {
      toast.error('Please fill in your name, email and a valid amount.');
      return;
    }
    try {
      setSubmitting(true);
      const res = await fetch(`${BACKEND_URL}/api/payments/paystack/initialize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          amount: amountAudNum,                   // AUD source amount
          student_name: studentName.trim(),
          phone: phone.trim() || null,
          payment_type: paymentType,
          selected_payment_currency: displayCurrency,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.authorization_url) {
        throw new Error(data?.detail || 'Could not start payment. Please try again.');
      }
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err.message || 'Could not start payment.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Seo
        title="Make a Payment | Winhouse Tutors"
        description="Secure online payment for tutoring, coaching and academic services."
        path="/pay"
        noindex
      />
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-28">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 mb-3">
            <ShieldCheck className="h-4 w-4" />
            Secure online payment
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Make a Payment</h1>
          <p className="mt-3 text-gray-600">
            Secure online payment for tutoring, coaching and academic services.
          </p>
        </div>

        <Card>
          <CardContent className="p-6 sm:p-8">
            <form className="space-y-5" onSubmit={handlePay} data-testid="paystack-pay-form">
              <div>
                <Label htmlFor="student_name">Student / Parent Name</Label>
                <Input id="student_name" value={studentName} onChange={(e) => setStudentName(e.target.value)} placeholder="Full name" data-testid="paystack-name" required />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="paystack-email" required />
                </div>
                <div>
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+61 4xx xxx xxx" data-testid="paystack-phone" />
                </div>
              </div>

              <div>
                <Label htmlFor="payment_type">Payment Type</Label>
                <select id="payment_type" value={paymentType} onChange={(e) => onTypeChange(e.target.value)}
                  className="w-full mt-1 border border-gray-300 rounded-md p-2 bg-white" data-testid="paystack-payment-type">
                  {(config.supported_payment_types || []).filter((t) => t !== 'donation').map((t) => (
                    <option key={t} value={t}>{PRODUCT_LABELS[t] || t}</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <Label htmlFor="amount" className="mb-0">Amount</Label>
                  <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-0.5 text-xs" role="group" aria-label="Display currency" data-testid="payment-currency-pills">
                    {CURRENCIES.map((c) => (
                      <button key={c.code} type="button" onClick={() => setDisplayCurrency(c.code)}
                        data-testid={`payment-currency-${c.code}`}
                        className={`px-3 py-1 rounded-full transition-colors ${displayCurrency === c.code ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Input id="amount" type="number" min="0.01" step="0.01" value={amountAud}
                  onChange={(e) => setAmountAud(e.target.value)} data-testid="paystack-amount" required />
                <p className="text-[11px] text-gray-500 mt-1">
                  AUD source amount. Default {PRODUCT_LABELS[paymentType] || paymentType}: AUD {Number(config.default_prices?.[paymentType] || 0).toFixed(2)}.
                </p>

                {amountAudNum > 0 && (
                  <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs space-y-1" data-testid="payment-breakdown">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount ({displayCurrency})</span>
                      <span className="font-semibold text-gray-900" data-testid="payment-displayed-amount">
                        {displayCurrency} {displayedAmount.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 pt-1.5">
                      <span className="text-gray-600">Checkout amount (USD)</span>
                      <span className="font-semibold text-emerald-700" data-testid="payment-usd-checkout">
                        USD {checkoutUsd.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <Button type="submit" disabled={submitting || loadingConfig} className="w-full btn-teal gap-2" data-testid="paystack-pay-now">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing…
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4" />
                    Pay {displayCurrency} {displayedAmount.toFixed(2)}
                  </>
                )}
              </Button>
              <p className="text-[10px] text-center text-gray-400">Payment processed securely · all amounts in AUD source-of-truth</p>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-gray-500 mt-6">
          Return to the <button type="button" onClick={() => navigate('/')} className="underline hover:text-gray-700">home page</button> anytime.
        </p>
      </main>

      <Footer />
      <FloatingActionButtons />
    </div>
  );
};

export default PaymentPage;
